import React, { useEffect, useMemo, useRef } from 'react';

// helpers
import useTranslation from './useTranslation';
import { styled } from 'styled-components';
import { signOut } from 'redux/actions/auth';
import { userAPI } from 'api/profile/userAPI';
import { darkTheme } from 'resources/theme/styled';
import { StateModel } from 'redux/reducers';
import { colorsTheme } from 'resources/theme/styled/colors';
import { useDispatch, useSelector } from 'react-redux';
import { setLastActivityTimestamp } from 'redux/actions/app';
import { StateModel as AppStateModel } from 'redux/reducers/app';
import { checkAppVersionAndRefreshIfNeeded } from 'helpers/commonHelpers';
import {
  setTimeOffset,
  getAccessToken,
  getRefreshToken,
  setAuthenticationTokens,
} from 'helpers/localStorageHelpers';

// components
import { Trans } from 'react-i18next';
import { Modal, Text } from '@ui';

const GRACE_PERIOD_SECONDS = 5;

export const useSessionTimeoutWatcher = ({
  sessionDuration,
}: {
  sessionDuration: number;
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const dispatch = useDispatch();

  const modalRef = useRef<ReturnType<typeof Modal.confirm> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { lastActivityTimestamp } = useSelector<StateModel, AppStateModel>(
    (state) => state.app,
  );

  const showWarningAfterMs = useMemo(() => {
    return (sessionDuration - 30) * 1000;
  }, [sessionDuration]);

  useEffect(() => {
    if (!sessionDuration) return;
    clearTimers();

    showTimeoutRef.current = setTimeout(() => {
      let remaining = 35;
      modalRef.current = Modal.confirm({
        width: 600,
        title: t('session_timeout_warning_popup.title'),
        content: (
          <Text variant="span">
            {t('session_timeout_warning_popup.description')}
          </Text>
        ),
        keyboard: false,
        okText: t('session_timeout_warning_popup.continue_session'),
        cancelText: t('session_timeout_warning_popup.log_out'),
        async onOk() {
          clearTimers();
          try {
            const { jwt, refreshToken, swaggerVersion, serverTime } =
              await userAPI.refreshTokens(getAccessToken(), getRefreshToken());
            setAuthenticationTokens(jwt, refreshToken);
            checkAppVersionAndRefreshIfNeeded(
              swaggerVersion,
              t('new_version_page_refresh', { ns: 'common' }),
            );
            setTimeOffset(serverTime);
            dispatch(setLastActivityTimestamp(Date.now()));
            return jwt;
          } catch {
            dispatch(signOut());
          }
        },
        onCancel() {
          clearTimers();
          dispatch(signOut());
        },
      });

      if (!modalRef.current) return;

      countdownTimerRef.current = setInterval(() => {
        if (modalRef.current) {
          const countdownValue = remaining - GRACE_PERIOD_SECONDS;
          modalRef.current.update({
            content: (
              <>
                <Text variant="span">
                  {t('session_timeout_warning_popup.description')}
                </Text>
                <AutoLogoutContainer theme={darkTheme}>
                  <Trans
                    t={t}
                    values={{
                      countdown: countdownValue < 0 ? 0 : countdownValue,
                    }}
                    i18nKey="session_timeout_warning_popup.auto_logout_description"
                    components={[
                      <Text
                        variant="span"
                        key="countdown"
                        weight="bold"
                        color={colorsTheme.colorWhite}
                      />,
                    ]}
                  />
                </AutoLogoutContainer>
              </>
            ),
          });
        }

        remaining -= 1;

        if (remaining < 0) {
          // stop the interval
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }

          // close the modal (if still open) and sign out
          if (modalRef.current) {
            modalRef.current.destroy();
            modalRef.current = null;
          }

          dispatch(signOut());
        }
      }, 1000);
    }, showWarningAfterMs);

    return () => {
      clearTimers();
    };
  }, [lastActivityTimestamp, showWarningAfterMs, dispatch]);

  const clearTimers = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (modalRef.current) {
      modalRef.current.destroy();
      modalRef.current = null;
    }
  };

  return null;
};

const AutoLogoutContainer = styled.div`
  font-size: ${({ theme }) => theme.fontSizeXs};
  margin-top: ${({ theme }) => theme.marginSm};
`;
